class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, .header__icon--cart');
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE' || event.code === 'Enter') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHTML = parsedState.sections && parsedState.sections[section.id];
      if (!sectionHTML) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

function openCartDrawer(triggerElement) {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  if (typeof drawer.open === 'function') {
    drawer.open(triggerElement || null);
  } else {
    drawer.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
  }
}

function initCartIconOpenDrawer() {
  document.addEventListener('click', function(e) {
    const cartIcon = e.target.closest('#cart-icon-bubble, .header__icon--cart');
    if (!cartIcon) return;
    e.preventDefault();
    e.stopPropagation();
    openCartDrawer(cartIcon);
  }, true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartIconOpenDrawer);
} else {
  initCartIconOpenDrawer();
}

class CartDrawerItems extends CartItems {
  // CartItems resets #Quantity-N, the cart page's id; the drawer's inputs are Drawer-quantity-N.
  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function initBestsellersCarousel(root) {
  const bestsellers = (root || document).querySelector('.cart-drawer__bestsellers');
  if (!bestsellers) return;

  const track = bestsellers.querySelector('.cart-drawer__bestsellers-products');
  const dotsContainer = bestsellers.querySelector('.cart-drawer__carousel-dots');
  const prevBtn = bestsellers.querySelector('.cart-drawer__carousel-btn--prev');
  const nextBtn = bestsellers.querySelector('.cart-drawer__carousel-btn--next');
  if (!track) return;

  const cards = Array.from(track.querySelectorAll('.cart-drawer__product-card'));
  const total = cards.length;
  if (total <= 1) {
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
    return;
  }

  let current = 0;

  function buildDots() {
    if (!dotsContainer) return;
    dotsContainer.innerHTML = '';
    cards.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className = 'cart-drawer__carousel-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('type', 'button');
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      dot.addEventListener('click', () => goTo(i));
      dotsContainer.appendChild(dot);
    });
  }

  function updateDots() {
    if (!dotsContainer) return;
    dotsContainer.querySelectorAll('.cart-drawer__carousel-dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === current);
    });
  }

  function updateButtons() {
    if (prevBtn) prevBtn.disabled = current === 0;
    if (nextBtn) nextBtn.disabled = current === total - 1;
  }

  function goTo(index) {
    current = Math.max(0, Math.min(total - 1, index));
    track.style.transform = 'translateX(-' + (current * 100) + '%)';
    updateDots();
    updateButtons();
  }

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

  buildDots();
  updateButtons();
  goTo(0);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initBestsellersCarousel());
} else {
  initBestsellersCarousel();
}

document.addEventListener('cart-drawer:rendered', () => initBestsellersCarousel());

const _originalRenderContents = CartDrawer.prototype.renderContents;
CartDrawer.prototype.renderContents = function(parsedState) {
  _originalRenderContents.call(this, parsedState);
  // After a successful add-to-cart the checkout button must never remain disabled.
  // This guards against two failure modes:
  // 1. A third-party app (e.g. Checkout+) re-disabling the button during widget init.
  // 2. The sections response being absent, leaving the original empty-cart HTML in place.
  const checkoutBtn = document.getElementById('CartDrawer-Checkout');
  if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
  // Also clear is-empty from cart-drawer-items so the CSS rule
  // `cart-drawer-items.is-empty + .drawer__footer { display:none }` doesn't hide the footer.
  const cartDrawerItems = this.querySelector('cart-drawer-items');
  if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');
  setTimeout(() => initBestsellersCarousel(this), 50);
};

document.addEventListener('click', function(e) {
  const btn = e.target.closest('.cart-drawer__add-to-cart');
  if (!btn || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const originalText = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Adding\u2026';

  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({
      id: parseInt(variantId),
      quantity: 1,
      sections: ['cart-drawer', 'cart-icon-bubble'],
    }),
  })
    .then((res) => res.json())
    .then((data) => {
      btn.textContent = 'Added!';

      if (data.sections) {
        const parser = new DOMParser();

        const cartDrawerHtml = data.sections['cart-drawer'];
        if (cartDrawerHtml) {
          const doc = parser.parseFromString(cartDrawerHtml, 'text/html');
          const newInner = doc.querySelector('.drawer__inner');
          const currentInner = document.querySelector('#CartDrawer .drawer__inner');
          if (newInner && currentInner) {
            currentInner.innerHTML = newInner.innerHTML;
            setTimeout(() => initBestsellersCarousel(currentInner), 50);
          }
        }

        const bubbleHtml = data.sections['cart-icon-bubble'];
        if (bubbleHtml) {
          const doc = parser.parseFromString(bubbleHtml, 'text/html');
          const newBubble = doc.querySelector('.shopify-section');
          const currentBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }

      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      }, 1800);
    })
    .catch(() => {
      btn.disabled = false;
      btn.textContent = originalText;
    });
});

// Empty cart tab switching
document.addEventListener('click', (e) => {
  const tab = e.target.closest('.cart-drawer__empty-tab');
  if (!tab) return;

  const container = tab.closest('.cart-drawer__empty-products');
  if (!container) return;

  const targetPanel = tab.dataset.tab;

  container.querySelectorAll('.cart-drawer__empty-tab').forEach((t) => {
    t.classList.toggle('is-active', t === tab);
    t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
  });

  container.querySelectorAll('.cart-drawer__empty-products-list').forEach((panel) => {
    panel.classList.toggle('is-hidden', panel.dataset.tabPanel !== targetPanel);
  });
});
